import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { pick, noop } from 'lodash';

import Loader from '../Loader';
import Icon from '../Icon';

import theme from './Button.scss';

const propTypes = {
  testId: PropTypes.string.isRequired,
  label: PropTypes.string,
  className: PropTypes.string,
  iconName: PropTypes.string,
  iconPositionLeft: PropTypes.bool,
  iconPositionRtlNotReversed: PropTypes.bool,
  iconDontFlipOnRtl: PropTypes.bool,
  isLoading: PropTypes.bool,
  buttonType: PropTypes.oneOf(['primary', 'naked', 'rounded', 'circle']),
  background: PropTypes.oneOf(['primary', 'green', 'greenMedium', 'red', 'orange']),
  labelColor: PropTypes.oneOf(['primary', 'green', 'greenMedium', 'red', 'orange', 'blackMedium']),
  size: PropTypes.oneOf(['large', 'medium', 'small', 'xsmall']),
  mediaResized: PropTypes.bool,
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  ariaLabel: PropTypes.string,
  children: PropTypes.node,
  tabIndex: PropTypes.number,
};

const defaultProps = {
  className: '',
  iconName: '',
  iconPositionLeft: false,
  iconPositionRtlNotReversed: false,
  iconDontFlipOnRtl: false,
  isLoading: false,
  buttonType: 'primary',
  background: 'primary',
  label: '',
  labelColor: 'primary',
  size: 'medium',
  mediaResized: false,
  fullWidth: false,
  disabled: false,
  ariaLabel: null,
  onClick: noop,
  children: null,
  tabIndex: undefined,
};

const Button = ({
  testId,
  label,
  className,
  iconName,
  iconPositionLeft,
  iconPositionRtlNotReversed,
  iconDontFlipOnRtl,
  isLoading,
  buttonType,
  background,
  labelColor,
  size,
  mediaResized,
  fullWidth,
  disabled,
  onClick,
  ariaLabel,
  children,
  ...rest
}) => {
  const buttonProps = {
    ...pick(rest, ['type', 'onClick', 'onKeyPress', 'tabIndex']),
    'data-testid': testId,
    'aria-label': ariaLabel || label,
    className: classnames(
      theme.root,
      {
        [theme[buttonType]]: buttonType,
        [theme.iconRight]: !!iconName && !iconPositionLeft,
        [theme.iconLeft]: !!iconName && iconPositionLeft,
        [theme.iconRtlNotReversed]: !!iconName && iconPositionRtlNotReversed,
        [theme[`bg_${ background }`]]: buttonType !== 'naked' && background,
        [theme[`color_${ labelColor }`]]: buttonType === 'naked' && labelColor,
        [theme[size]]: !!size,
        [theme.mediaResized]: mediaResized,
        [theme.fullWidth]: fullWidth,
      }, className,
    ),
    disabled: disabled || isLoading,
    onClick,
    onMouseDown: e => e.preventDefault(),
  };

  return (
    <button { ...buttonProps }>
      <div className={ classnames(theme.iconContainer, { [theme.iconDontFlipOnRtl]: iconDontFlipOnRtl }) }>
        { isLoading ?
          <Loader height={ size === 'small' ? 20 : undefined } width={ size === 'small' ? 20 : undefined } /> :
          iconName && <Icon name={ iconName } />
        }
      </div>
      { children || <span>{ label }</span> }
    </button>
  );
};

Button.propTypes = propTypes;
Button.defaultProps = defaultProps;

export default Button;
